import TextButton from 'LuxKit/Button/TextButton'
import LabelGroup from 'LuxKit/Label/LabelGroup'
import Tour2027TravelTagLabel from 'components/Tours/Tour2027TravelTagLabel'
import TourProductTypeLabel from 'components/Tours/TourProductTypeLabel'
import TourV2UrgencyTags from 'components/Tours/TourV2UrgencyTags'
import useOfferUrl from 'hooks/Offers/useOfferUrl'
import useSearchTourVariation from 'hooks/TourV2/useSearchTourVariation'
import { EmptyArray } from 'lib/array/arrayUtils'
import { EmptyObject } from 'lib/object/objectUtils'
import { isOfferRatingDisplayable } from 'lib/order/reviewUtils'
import { useContext, useMemo } from 'react'
import CurrencyContext from 'contexts/currencyContext'
import BookmarkButton from 'tripPlanner/components/Bookmark/BookmarkButton'
import type { SearchTileLogo } from '../SearchTile'
import SearchTile from '../SearchTile'
import SearchTileRowOverlineJourney from '../shared/SearchTileRowOverlineJourney'
import TourV2SearchTileFeatures from './TourV2SearchTileFeatures'
import TourV2SearchTileMeta from './TourV2SearchTileMeta'
import TourV2SearchTilePriceStack from './TourV2SearchTilePriceStack'
import SolidStarCircleIcon from 'LuxKit/Icons/solid/SolidStarCircleIcon'
import StructureLabel from 'LuxKit/Label/StructureLabel'
import { isTourLimitedTimeOffer } from 'lib/tours/tourUtils'
import findCheapestTourV2PurchasableOption from 'lib/tours/findCheapestTourV2PurchasableOption'
import { CRUISE_FLASH_OFFER_TYPE } from 'constants/cruise'
import { useCurrentSiteTakeover } from 'takeovers/utils/hooks/useCurrentSiteTakeover'
import { useIntl } from 'react-intl'
import { useAppSelector } from 'hooks/reduxHooks'
import getTasteOfGoldPromoCampaign from 'takeovers/tasteOfGoldTakeover/selectors/getTasteOfGoldPromoCampaign'
import isTasteOfGoldEligibleOffer from 'takeovers/tasteOfGoldTakeover/utils/isTasteOfGoldEligibleOffer'
import TasteOfGoldTileBenefitMessage from 'takeovers/tasteOfGoldTakeover/components/TasteOfGoldTileBenefitMessage'
import Group from 'components/utils/Group'
import LtleTooltipWrapper from 'components/Common/Labels/LtleTooltipWrapper'
interface Props {
  offer: App.Tours.TourV2Offer | App.Tours.TourV2OfferSummary
  filters?: App.OfferListFilters
}

function TourV2SearchTile(props: Props) {
  const { offer, filters = EmptyObject }: Props = props
  const intl = useIntl()
  const variation = useSearchTourVariation(offer, filters)

  const offerUrl = useOfferUrl(offer, {
    filters,
    offerLinkIncludesFilters: true,
  })

  const isLimitedTimeOffer = isTourLimitedTimeOffer(offer)

  const cruiseFlashOfferLogo = useMemo((): SearchTileLogo | undefined => {
    const isCruiseFlashOffer = offer.productType === CRUISE_FLASH_OFFER_TYPE
    if (!isCruiseFlashOffer) {
      return
    }

    return {
      compact: { id: offer.brand.logoImage?.id, title: offer.brand.name },
      full: { id: offer.brand.logoImage?.id, title: offer.brand.name },
    }
  }, [offer])

  const cheapestOptionPrice = useMemo(() => {
    if (variation) {
      return findCheapestTourV2PurchasableOption(offer, variation.id)?.price ?? 0
    }
    return 0
  }, [offer, variation])

  const currency = useContext(CurrencyContext)
  const offerSiteTakeover = useCurrentSiteTakeover({ offer })
  const isTasteOfGoldActive = !!useAppSelector(getTasteOfGoldPromoCampaign)

  const viewOfferLabel = intl.formatMessage({
    id: 'xa7lFjSZ_W3p7-P1ruu4',
    defaultMessage: 'View offer',
    description: 'Button text to view tour offer details on the tour offer detail page',
  })
  const limitedTimeLuxExclusiveLabel = intl.formatMessage({
    id: 'oy04obfoQc7DwbXmVE_I',
    defaultMessage: 'Limited Time Lux Exclusive',
    description: 'Label for limited time exclusive tour offers on the tour page',
  })
  const showTasteOfGoldBenefit =
    isTasteOfGoldActive &&
    isTasteOfGoldEligibleOffer(offer, currency, !!variation?.countriesVisited.some(c => c.toLowerCase() !== 'australia'), cheapestOptionPrice)

  return <SearchTile
    banner={offerSiteTakeover?.searchPage?.tileBanner}
    action={<TextButton nonInteractable kind="primary">{viewOfferLabel}</TextButton>}
    bookmarkAction={<BookmarkButton offer={offer} />}
    features={variation ? <TourV2SearchTileFeatures variation={variation} offer={offer} filters={filters} /> : undefined}
    images={variation?.images ?? EmptyArray}
    labels={<TourV2UrgencyTags offer={offer} suffixLabels={<Tour2027TravelTagLabel tourId={offer.id} />} />}
    meta={<Group direction="vertical" gap={8}>
      <TourV2SearchTileMeta variation={variation} offer={offer} filters={filters} />
      {showTasteOfGoldBenefit && <TasteOfGoldTileBenefitMessage />}
    </Group>}
    overline={variation?.startLocation && variation?.endLocation ? <SearchTileRowOverlineJourney start={variation.startLocation} end={variation.endLocation} /> : undefined}
    pricePoint={<TourV2SearchTilePriceStack offer={offer} filters={filters} variation={variation} />}
    productLabel={<LabelGroup>
      {isLimitedTimeOffer &&
        <LtleTooltipWrapper>
          <StructureLabel startIcon={<SolidStarCircleIcon />} variant="exclusive">{limitedTimeLuxExclusiveLabel}</StructureLabel>
        </LtleTooltipWrapper>}
      <TourProductTypeLabel productType={offer.productType} offerUrl={offerUrl} />
    </LabelGroup>}
    rating={isOfferRatingDisplayable(offer.rating, 0) ? offer.rating : undefined}
    title={offer.name}
    logo={cruiseFlashOfferLogo}
    offer={offer}
  />
}

export default TourV2SearchTile
